import { type Consumer, createConsumer } from "@rails/actioncable"

/**
 * OKS-406 — Singleton ActionCable consumer for the creator real-time
 * layer.
 *
 * `createConsumer()` with no argument connects to the default `/cable`
 * mount (see `config/routes.rb`); the connection identity is the signed
 * session cookie, resolved server-side in `ApplicationCable::Connection`
 * — the client never names a stream. The underlying consumer
 * auto-reconnects with backoff out of the box, which is the entire
 * "reconnect logic" the feature needs (UX Spec §3 "Reconnecting").
 *
 * One consumer per tab is sufficient and cheap; we memoise it so every
 * hook mount shares the same WebSocket rather than opening a new socket
 * per subscription.
 *
 * Lives behind a getter (not a module-level constant) so that import of
 * this module never eagerly opens a socket — the consumer is created on
 * first subscribe, keeping non-creator surfaces socket-free.
 */
let consumer: Consumer | undefined

// OKS-491 — Window augmentation for the test-support reconnect handle.
// `__cableConsumer` is the singleton consumer, exposed ONLY under the
// server-rendered test-support flag (see `testSupportEnabled` below).
interface CableTestWindow {
  __cableConsumer?: Consumer
}

/**
 * OKS-491 — True only when the app layout rendered the
 * `<meta name="test-support" content="1">` signal — which
 * `ApplicationHelper#test_support_enabled?` emits ONLY on the Playwright
 * Rails server (`Rails.env.test?` AND `RAILS_TEST_SUPPORT_ENABLED=1`). The tag
 * is absent in dev/prod and under the default RSpec run, so the
 * `window.__cableConsumer` handle below can never leak into a real session.
 */
function testSupportEnabled(): boolean {
  if (typeof document === "undefined") return false
  return (
    document
      .querySelector('meta[name="test-support"]')
      ?.getAttribute("content") === "1"
  )
}

export function getCableConsumer(): Consumer {
  consumer ??= createConsumer()

  // OKS-491 — Under the test-support flag ONLY, expose the singleton consumer
  // on `window` so `login-toast-once-oks491.spec.ts` can deterministically
  // force a genuine `CreatorChannel` reconnect via
  // `window.__cableConsumer.connection.reopen()`. This drives the REAL
  // reconnect → real `connected()` → real `router.reload({ only: ... })`
  // partial-reload path — nothing under test (flash/Inertia interplay) is
  // mocked. Preferred over the CDP offline-toggle, whose backoff-driven
  // reconnect (~3–15s) flakes under Playwright's `retries:0`. Never reached in
  // dev/prod — the gating `<meta>` is server-rendered test-only.
  if (testSupportEnabled() && typeof window !== "undefined") {
    ;(window as unknown as CableTestWindow).__cableConsumer = consumer
  }

  return consumer
}
